import React, { useState } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Box,
} from "@mui/material";
import { generateSecretMessageLink } from "../services/ApiService";
import {constants} from "../services/constants";
import { useTranslation } from "react-i18next";

const SecretMessage = ({ onLinkGenerated }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { t, i18n } = useTranslation();

  const BASE_URL = constants.FRONTEND_URL

  const generateLink = async () => {
    if (!name.trim()) {
      setError(t('secretMessage.errors.nameRequired'));
      return;
    }

    if (!agreedToTerms) {
      setError(t('secretMessage.errors.termsRequired'));
      return;
    }

    setError("");
    setLoading(true);

    try {
      const userId = await generateSecretMessageLink(name);
      console.log("Generated User ID:", userId);
      // Get current language and add to URL
      const currentLang = i18n.language || 'en';
      const generatedLink = `${BASE_URL}${currentLang}/secret/${userId}`;
      onLinkGenerated(generatedLink);
      localStorage.setItem("userId", userId);
    } catch (error) {
      console.error("Error in generateLink:", error.message);
      setError(t('secretMessage.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ p: 4, mt: { xs: "8", sm: 4 }, mb: 6 }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
      >
        {t('secretMessage.title')}
      </Typography>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 800,
          width: "100%",
          mb: 2,
          borderRadius:"10px",
          border:"2px solid gray"
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontSize: { xs: "1rem", sm: "1.44rem" } }}
        >
          {t('secretMessage.subtitle1')}
        </Typography>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontSize: { xs: "1rem", sm: "1.44rem" } }}
        >
          {t('secretMessage.subtitle2')}
        </Typography>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontSize: { xs: "1rem", sm: "1.44rem" } }}
        >
          {t('secretMessage.subtitle3')}
        </Typography>
      </Paper>
      <Box sx={{ maxWidth: 800, width: "100%", borderRadius: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label={t('secretMessage.nameField')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={() => setAgreedToTerms(!agreedToTerms)}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.8rem", sm: "1rem" } }}
            >
              {t('secretMessage.termsAgreement')}
            </Typography>
          }
        />

        {error && (
          <Typography
            variant="body2"
            color="error"
            textAlign="center"
            gutterBottom
            sx={{ mt: 2 }}
          >
            {error}
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={generateLink}
          disabled={loading}
          sx={{
            mt: 2,
            py: 2,
            fontSize: { xs: "1rem", sm: "1.25rem" },
            fontWeight: "bold",
          }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t('secretMessage.generateButton')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default SecretMessage;
